import React from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useGetDashboardStatsQuery, useGetContactsQuery, useGetMessagesQuery } from '../store/services/whatsappApi';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DashboardStats {
  total_contacts: number;
  active_contacts: number;
  total_messages: number;
  delivered_messages: number;
  read_messages: number;
  response_rate: number;
  campaigns?: {
    id: number;
    name: string;
    status: string;
    target_contacts: number;
    sent_messages: number;
    created_at: string;
  }[];
}

const Dashboard: React.FC = () => {
  const { data: stats, isLoading: isStatsLoading } = useGetDashboardStatsQuery();
  const { data: contacts } = useGetContactsQuery({ limit: 10000 });
  const { data: messagesResponse } = useGetMessagesQuery({ limit: 1000 });
  const messages = messagesResponse?.messages || [];
  return (
    <MainLayout>
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold text-whatsapp-teal mb-6">Dashboard</h1>
        
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-whatsapp-light-green text-whatsapp-green">
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                  <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                </svg>
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600 text-sm font-medium">Messages</h2>
                {isStatsLoading ? (
                  <div className="animate-pulse h-6 w-20 bg-gray-200 rounded"></div>
                ) : (
                  <p className="text-2xl font-semibold text-gray-800">{stats?.total_messages || 0}</p>
                )}
                <Link to="/messages" className="text-whatsapp-green text-sm hover:underline">View all messages</Link>
              </div>
            </div>
          </div>
          
          <div className="card bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-whatsapp-light-green text-whatsapp-green">
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600 text-sm font-medium">Contacts</h2>
                {isStatsLoading ? (
                  <div className="animate-pulse h-6 w-20 bg-gray-200 rounded"></div>
                ) : (
                  <p className="text-2xl font-semibold text-gray-800">{stats?.total_contacts || contacts?.contacts?.length || 0}</p>
                )}
                <Link to="/contacts" className="text-whatsapp-green text-sm hover:underline">Manage contacts</Link>
              </div>
            </div>
          </div>
          
          <div className="card bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-whatsapp-light-green text-whatsapp-green">
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                </svg>
              </div>
              <div className="ml-4">
                <h2 className="text-gray-600 text-sm font-medium">Active Campaigns</h2>
                {isStatsLoading ? (
                  <div className="animate-pulse h-6 w-20 bg-gray-200 rounded"></div>
                ) : (
                  <p className="text-2xl font-semibold text-gray-800">{stats?.active_campaigns || 0}</p>
                )}
                <Link to="/campaigns" className="text-whatsapp-green text-sm hover:underline">View campaigns</Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Recent Messages */}
        <div className="card mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-whatsapp-teal">Recent Messages</h2>
            <Link to="/messages" className="text-sm text-whatsapp-green hover:text-whatsapp-dark-green hover:underline">View All</Link>
          </div>
          
          {isStatsLoading ? (
            <div className="animate-pulse space-y-4 mb-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center h-16 px-4 border border-gray-100 rounded-md">
                  <div className="rounded-full bg-gray-200 h-10 w-10 mr-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-100 rounded w-48"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {messages && messages.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {messages.slice(0, 5).map((message) => (
                      <tr key={message.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-whatsapp-green flex items-center justify-center text-white">
                              <span>{message.contact?.name?.charAt(0) || '?'}</span>
                            </div>
                            <div className="ml-4">
                              <Link to={`/messages/${message.contact_id}`} className="text-sm font-medium text-gray-900 hover:underline">{message.contact?.name || 'Unknown'}</Link>
                              <div className="text-sm text-gray-500">{message.contact?.phone_number || 'No phone'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">{message.content}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {(() => {
                              try {
                                // Try with timestamp first
                                if (message.timestamp) {
                                  const date = new Date(message.timestamp);
                                  if (!isNaN(date.getTime())) {
                                    return date.toLocaleString();
                                  }
                                }
                                // Fall back to created_at if timestamp invalid
                                if (message.created_at) {
                                  const date = new Date(message.created_at);
                                  if (!isNaN(date.getTime())) {
                                    return date.toLocaleString();
                                  }
                                }
                                return 'Pending';
                              } catch {
                                return 'Pending';
                              }
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${message.status === 'delivered' ? 'bg-green-100 text-green-800' : message.status === 'sent' ? 'bg-blue-100 text-blue-800' : message.status === 'read' ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'}`}>
                            {message.status?.charAt(0).toUpperCase() + message.status?.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-500 mb-4">No messages found</p>
                  <Link to="/contacts" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-whatsapp-green hover:bg-whatsapp-dark-green">
                    Start messaging
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Active Campaigns */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-whatsapp-teal">Active Campaigns</h2>
            <Link to="/campaigns" className="text-sm text-whatsapp-green hover:text-whatsapp-dark-green hover:underline">View All</Link>
          </div>
          
          {isStatsLoading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2].map(i => (
                <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="space-y-2 w-2/3">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-3 bg-gray-100 rounded w-48"></div>
                  </div>
                  <div className="w-24 h-2 bg-gray-200 rounded-full"></div>
                </div>
              ))}
            </div>
          ) : (stats as any)?.campaigns ? (
            <div className="space-y-4">
              {(stats as any).campaigns.slice(0, 3).map((campaign: any) => (
                <div key={campaign.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div>
                    <h3 className="text-md font-medium text-gray-900">{campaign.name}</h3>
                    <p className="text-sm text-gray-500">
                      Sent: {campaign.sent_count || 0} • 
                      Delivered: {campaign.delivered_count || 0} • 
                      Read: {campaign.read_count || 0}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-4">
                      <div className="text-right text-sm font-medium text-gray-900">
                        {campaign.delivered_count && campaign.sent_count 
                          ? Math.round((campaign.delivered_count / campaign.sent_count) * 100) 
                          : 0}%
                      </div>
                      <div className="w-24 h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 bg-whatsapp-green rounded-full" 
                          style={{ width: `${campaign.delivered_count && campaign.sent_count 
                            ? Math.round((campaign.delivered_count / campaign.sent_count) * 100) 
                            : 0}%` }}
                        ></div>
                      </div>
                    </div>
                    <Link to={`/campaigns/${campaign.id}`} className="text-whatsapp-green hover:text-whatsapp-dark-green">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">No active campaigns</p>
              <Link to="/campaigns/new" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-whatsapp-green hover:bg-whatsapp-dark-green">
                Create campaign
              </Link>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
